import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';
import { Player, Team } from '../types';
import { Position, DepthChartEntry, Formations } from '../types/depth-chart';
import { SPORT_POSITIONS, SPECIAL_TEAMS_POSITIONS } from '../data/sportsPositions';
import { 
  FOOTBALL_OFFENSIVE_FORMATIONS, 
  FOOTBALL_DEFENSIVE_FORMATIONS, 
  FOOTBALL_SPECIAL_TEAMS_FORMATIONS 
} from '../data/sportsFormations';
import { SPORT_RULESETS } from '../data/sportRulesets';
import { LinemenPositions } from '../types/positions';
import { shouldAutoSubstitutePlayer, type InjuryStatus } from '../constants/injuries';
import { getPositionGroupsForLabel } from '../services/positionMappingService';

interface UseDepthChartProps {
  currentTeam: Team | null;
  activeTab: string;
  formations: Formations;
}

// No special teams unit function needed - we just use the formation field

export const useDepthChart = ({ currentTeam, activeTab, formations }: UseDepthChartProps) => {
  const [players, setPlayers] = useState<Player[]>([]);
  const [depthChart, setDepthChart] = useState<DepthChartEntry[]>([]);
  const [positions, setPositions] = useState<Position[]>([]);
  const [customPositions, setCustomPositions] = useState<Position[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedDepth, setSelectedDepth] = useState(1);
  
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const dedupePositionsByKey = useCallback((input: Position[]): Position[] => {
    return input.filter((position, index, array) =>
      array.findIndex(p => p.key === position.key) === index
    );
  }, []);

  // Database `depth_chart.unit` has a constrained enum/check.
  // Some UI tabs (e.g. "Team", "Midfield") are view-layer concepts and must
  // be mapped to a valid persisted unit for reads/writes.
  const getDatabaseUnitForTab = useCallback((tab: string): string => {
    const allowedUnits = new Set(['Offense', 'Defense', 'Special Teams', 'Lineup', 'Fielding']);
    if (allowedUnits.has(tab)) return tab;
    // Fallback for single-tab/team views (e.g. Basketball/Hockey "Team")
    return 'Offense';
  }, []);

  // Helper to get sport ruleset
  const getSportRuleset = useCallback(() => {
    if (!currentTeam?.sport) return null;
    return SPORT_RULESETS[currentTeam.sport as keyof typeof SPORT_RULESETS];
  }, [currentTeam?.sport]);

  // Helper function to check if a formation is a standard (predefined) formation
  const isStandardFormation = useCallback((formationName: string): boolean => {
    if (!formationName || !currentTeam) return true;
    
    // Check against predefined formations for the current sport and tab
    const sportRuleset = getSportRuleset();
    if (!sportRuleset?.formations.enabled) return true;
    
    if (currentTeam.sport === 'Football') {
      if (activeTab === 'Offense') {
        return FOOTBALL_OFFENSIVE_FORMATIONS.some(f => f.name === formationName);
      } else if (activeTab === 'Defense') {
        return FOOTBALL_DEFENSIVE_FORMATIONS.some(f => f.name === formationName);
      } else if (activeTab === 'Special Teams') {
        return FOOTBALL_SPECIAL_TEAMS_FORMATIONS.some(f => f.name === formationName);
      }
    }
    // Add other sports as needed
    
    return false; // If not found in predefined formations, it's custom
  }, [currentTeam, activeTab, getSportRuleset]);

  // Fetch players
  const fetchPlayers = useCallback(async () => {
    if (!currentTeam) return;

    try {
      setIsLoading(true);
      
      const { data, error } = await supabase
        .from('players')
        .select(`
          id,
          first_name,
          last_name,
          number,
          position,
          height,
          weight,
          team_id,
          injury_status,
          play_participation,
          is_sub
        `)
        .eq('team_id', currentTeam.id);
      
      if (error) {
        console.error('Error fetching players:', error);
        setPlayers([]);
      } else {
        // Fetch player cards to get eligible positions and can_play_line flag
        const { data: playerCardsData } = await supabase
          .from('player_cards')
          .select('player_id, eligible_positions, can_play_line, role_types')
          .eq('team_id', currentTeam.id);

        // Map players with proper playParticipation data and eligible positions
        const playersWithParticipation = (data || []).map(player => {
          const card = playerCardsData?.find(c => c.player_id === player.id);
          return {
            ...player,
            playParticipation: player.play_participation,
            eligible_positions: card?.eligible_positions || [],
            role_types: card?.role_types || [],
            can_play_line: card?.can_play_line || false,
          };
        });
        setPlayers(playersWithParticipation);
      }
    } catch (error) {
      console.error('Error in fetchPlayers:', error);
    } finally {
      setIsLoading(false);
    }
  }, [currentTeam]);

  // Fetch custom positions
  const fetchCustomPositions = useCallback(async () => {
    if (!currentTeam?.id) {
      setCustomPositions([]);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('custom_positions')
        .select('*')
        .eq('team_id', currentTeam.id);

      if (error) {
        console.error('Error fetching custom positions:', error);
        setCustomPositions([]);
      } else {
        setCustomPositions(data || []);
      }
    } catch (error) {
      console.error('Error fetching custom positions:', error);
      setCustomPositions([]);
    }
  }, [currentTeam?.id]);

  // Sort positions by color and type
  const sortPositions = useCallback((positions: Position[], depthChartData: DepthChartEntry[]): Position[] => {
    // Get color for each position at current depth
    const getPositionColor = (position: Position): string => {
      const entry = depthChartData.find(
        e => e.position === position.key && e.depth === selectedDepth && e.unit === activeTab
      );
      return entry?.color || '';
    };

    // Define linemen positions for football
    const isLineman = (position: Position): boolean => {
      if (currentTeam?.sport === 'Football') {
        return LinemenPositions.includes(position.key as any);
      }
      return false;
    };

    return [...positions].sort((a, b) => {
      // Special handling for baseball/softball lineup - sort numerically by batting order
      if ((currentTeam?.sport === 'Baseball' || currentTeam?.sport === 'Softball') && activeTab === 'Lineup') {
        const numA = parseInt(a.key, 10);
        const numB = parseInt(b.key, 10);
        return numA - numB; // Numerical sort: 1, 2, 3, 4, ... 10, 11, 12, etc.
      }

      const colorA = getPositionColor(a);
      const colorB = getPositionColor(b);

      // First sort by color (positions with colors come first)
      if (colorA && !colorB) return -1;
      if (!colorA && colorB) return 1;
      if (colorA && colorB) {
        // If both have colors, sort by color value
        return colorA.localeCompare(colorB);
      }

      // If no colors, sort by skill position vs linemen
      const isLinemanA = isLineman(a);
      const isLinemanB = isLineman(b);
      if (!isLinemanA && isLinemanB) return -1; // Skill positions first
      if (isLinemanA && !isLinemanB) return 1;  // Linemen second

      // If same type, sort by position key
      return a.key.localeCompare(b.key);
    });
  }, [currentTeam?.sport, activeTab, selectedDepth]);

  // Resolve default positions for the current sport/tab.
  // Consolidates duplicated tab-to-position mapping logic used in loadDepthChart.
  const getDefaultPositionsForCurrentTab = useCallback((): Position[] => {
    if (!currentTeam) return [];

    if (currentTeam.sport === 'Football') {
      if (activeTab === 'Offense') {
        const formation = FOOTBALL_OFFENSIVE_FORMATIONS.find(f => f.name === formations.selectedFormation);
        return formation?.positions || SPORT_POSITIONS.Football.Offense;
      }
      if (activeTab === 'Defense') {
        const formation = FOOTBALL_DEFENSIVE_FORMATIONS.find(f => f.name === formations.selectedFormation);
        return formation?.positions || SPORT_POSITIONS.Football.Defense;
      }
      if (activeTab === 'Special Teams') {
        // Use selected formation for special teams instead of hardcoded default.
        const formation = FOOTBALL_SPECIAL_TEAMS_FORMATIONS.find(f => f.name === formations.selectedFormation);
        return formation?.positions || SPECIAL_TEAMS_POSITIONS['Default'] || [];
      }
      return [];
    }

    const sportPositions = SPORT_POSITIONS[currentTeam.sport];
    if (!sportPositions) return [];

    // Baseball/Softball uses Lineup/Fielding tabs that map to Offense/Defense sets.
    if (
      (currentTeam.sport === 'Baseball' || currentTeam.sport === 'Softball') &&
      activeTab === 'Lineup'
    ) {
      return sportPositions.Offense || [];
    }
    if (
      (currentTeam.sport === 'Baseball' || currentTeam.sport === 'Softball') &&
      activeTab === 'Fielding'
    ) {
      return sportPositions.Defense || [];
    }

    if (sportPositions[activeTab]) {
      return sportPositions[activeTab] || [];
    }

    // Fallback for single-tab sports (e.g., Basketball's `Team` tab).
    const merged = Object.values(sportPositions).flat();
    return merged.filter((p, index, arr) => arr.findIndex(x => x.key === p.key) === index);
  }, [currentTeam, activeTab, formations.selectedFormation]);

  // Load depth chart data
  const loadDepthChart = useCallback(async (preservePositions?: Position[]) => {
    if (!currentTeam?.id) {
      setDepthChart([]);
      return;
    }

    const databaseUnit = getDatabaseUnitForTab(activeTab);
    console.log('[loadDepthChart] Loading for team:', currentTeam.id, 'tab:', activeTab, 'dbUnit:', databaseUnit, 'formation:', formations.selectedFormation);

    try {
      // Build query to fetch formation-specific depth chart data
      let query = supabase
        .from('depth_chart')
        .select('*')
        .eq('team_id', currentTeam.id)
        .eq('unit', databaseUnit)
        .order('position', { ascending: true })
        .order('depth', { ascending: true });

      // Add formation filter if sport uses formations
      const sportRuleset = getSportRuleset();
      if (sportRuleset?.formations.enabled && formations.selectedFormation) {
        query = query.eq('formation', formations.selectedFormation);
      }

      const { data: depthChartData, error } = await query;

      if (error) {
        console.error('Error fetching depth chart:', error);
        return;
      }

      console.log('[loadDepthChart] Found', depthChartData?.length || 0, 'entries for formation:', formations.selectedFormation);

      // Check for injured players and auto-substitute if needed
      let processedDepthChartData = (depthChartData || []).map(entry => ({
        ...entry,
        // Keep UI comparisons consistent (components compare with activeTab).
        unit: activeTab
      }));
      if (players.length > 0 && processedDepthChartData.length > 0) {
        console.log('[loadDepthChart] Checking for injured players in depth chart...');
        
        const injuredPlayersInDepthChart = processedDepthChartData.filter(entry => {
          if (!entry.player_id) return false;
          const player = players.find(p => p.id === entry.player_id);
          return player && shouldAutoSubstitutePlayer(player.injury_status as InjuryStatus);
        });

        if (injuredPlayersInDepthChart.length > 0) {
          console.log('[loadDepthChart] Found', injuredPlayersInDepthChart.length, 'injured players requiring substitution');
          
          // Track promotions for notification
          const promotions: Array<{position: string, fromPlayer: string, toPlayer: string}> = [];
          
          // Process each injured player's position
          for (const injuredEntry of injuredPlayersInDepthChart) {
            const position = injuredEntry.position;
            const depth = injuredEntry.depth;
            
            // Find the next available backup at this position
            const backupsAtPosition = processedDepthChartData
              .filter(entry => 
                entry.position === position && 
                entry.unit === activeTab &&
                entry.depth > depth &&
                entry.player_id // Make sure there's actually a player assigned
              )
              .sort((a, b) => a.depth - b.depth); // Sort by depth to get the next backup
            
            if (backupsAtPosition.length > 0) {
              const nextBackup = backupsAtPosition[0];
              const promotedPlayer = players.find(p => p.id === nextBackup.player_id);
              const injuredPlayer = players.find(p => p.id === injuredEntry.player_id);
              
              if (promotedPlayer && injuredPlayer) {
                // Promote the backup to the injured player's depth
                nextBackup.depth = depth;
                
                // Remove the injured player
                processedDepthChartData = processedDepthChartData.filter(entry => 
                  !(entry.player_id === injuredEntry.player_id && entry.position === position && entry.unit === activeTab)
                );
                
                // Track the promotion
                promotions.push({
                  position,
                  fromPlayer: `${injuredPlayer.first_name} ${injuredPlayer.last_name}`,
                  toPlayer: `${promotedPlayer.first_name} ${promotedPlayer.last_name}`
                });
                
                console.log(`[loadDepthChart] Promoted ${promotedPlayer.first_name} ${promotedPlayer.last_name} from depth ${nextBackup.depth} to depth ${depth} at position ${position}`);
              }
            } else {
              // No backup available, just remove the injured player
              processedDepthChartData = processedDepthChartData.filter(entry => 
                !(entry.player_id === injuredEntry.player_id && entry.position === position && entry.unit === activeTab)
              );
            }
          }

          console.log('[loadDepthChart] Updated depth chart entries:', processedDepthChartData.length, 'vs original:', depthChartData?.length || 0);

          // Save the updated depth chart to database
          try {
            const sportRuleset = getSportRuleset();
            if (sportRuleset?.formations.enabled && formations.selectedFormation) {
              // Delete injured player entries for this formation
              await supabase
                .from('depth_chart')
                .delete()
                .eq('team_id', currentTeam.id)
                .eq('unit', activeTab)
                .eq('formation', formations.selectedFormation)
                .in('player_id', injuredPlayersInDepthChart.map(e => e.player_id).filter(Boolean));
              
              // Update promoted players in database
              for (const entry of processedDepthChartData) {
                if (entry.id) {
                  await supabase
                    .from('depth_chart')
                    .update({ depth: entry.depth })
                    .eq('id', entry.id);
                }
              }
            } else {
              // Delete injured player entries for this tab
              await supabase
                .from('depth_chart')
                .delete()
                .eq('team_id', currentTeam.id)
                .eq('unit', activeTab)
                .in('player_id', injuredPlayersInDepthChart.map(e => e.player_id).filter(Boolean));
              
              // Update promoted players in database
              for (const entry of processedDepthChartData) {
                if (entry.id) {
                  await supabase
                    .from('depth_chart')
                    .update({ depth: entry.depth })
                    .eq('id', entry.id);
                }
              }
            }

            // Show notification to user
            const injuredPlayerNames = injuredPlayersInDepthChart.map(entry => {
              const player = players.find(p => p.id === entry.player_id);
              return player ? `${player.first_name} ${player.last_name}` : 'Unknown Player';
            }).join(', ');
            
            let notificationMessage = `Removed injured players: ${injuredPlayerNames}`;
            
            if (promotions.length > 0) {
              const promotionDetails = promotions.map(p => 
                `${p.toPlayer} promoted to ${p.position} (replacing ${p.fromPlayer})`
              ).join(', ');
              notificationMessage += `. Promotions: ${promotionDetails}`;
            }
            
            toast.success(notificationMessage, { duration: 6000 });
          } catch (error) {
            console.error('Error saving auto-substitution to database:', error);
            toast.error('Failed to save auto-substitution changes');
          }
        }
      }

      setDepthChart(processedDepthChartData);

      // If preservePositions is provided, use those positions but update colors from database
      if (preservePositions) {
        console.log('[loadDepthChart] Using preserved positions with updated colors');
        const updatedPositions = preservePositions.map(position => {
          const dbEntry = depthChartData?.find(e => e.position === position.key.toUpperCase());
          return {
            ...position,
            color: dbEntry?.color || ''
          };
        });
        
        // Ensure no duplicate position keys
        const deduplicatedUpdatedPositions = dedupePositionsByKey(updatedPositions);
        
        setPositions(deduplicatedUpdatedPositions);
        return;
      }

      if (!sportRuleset) {
        console.error('No sport ruleset found for:', currentTeam.sport);
        return;
      }

      // For custom formations, build positions from database + defaults
      const isCustomFormation = formations.selectedFormation && !isStandardFormation(formations.selectedFormation);
      
      if (isCustomFormation || depthChartData?.length > 0) {
        console.log('[loadDepthChart] Building positions from database and defaults');
        
        // Get unique positions from database
        const dbPositions = Array.from(new Set(depthChartData?.map(entry => entry.position.toUpperCase()) || []));
        
        // Create position objects with database colors and full names
        const finalPositions: Position[] = dbPositions.map(positionKey => {
          const dbEntry = depthChartData?.find(e => e.position.toUpperCase() === positionKey);
          return {
            key: positionKey,
            name: positionKey,
            fullName: dbEntry?.position_full_name || positionKey,
            color: dbEntry?.color || ''
          };
        });

        // Add default positions if we have fewer than 11
        if (finalPositions.length < 11) {
          const defaultPositions = getDefaultPositionsForCurrentTab();
          
          // Add missing default positions
          const existingKeys = finalPositions.map(p => p.key);
          const neededDefaults = defaultPositions.filter(def => !existingKeys.includes(def.key));
          const toAdd = neededDefaults.slice(0, 11 - finalPositions.length);
          
          finalPositions.push(...toAdd);
        }

        // Ensure no duplicate position keys before sorting
        const deduplicatedPositions = dedupePositionsByKey(finalPositions);
        
        const sortedPositions = sortPositions(deduplicatedPositions, depthChartData || []);
        setPositions(sortedPositions);
      } else {
        // No custom data, use default formation positions
        console.log('[loadDepthChart] Using default formation positions');
        const defaultPositions = getDefaultPositionsForCurrentTab();
        
        // Ensure no duplicate position keys
        const deduplicatedDefaultPositions = dedupePositionsByKey(defaultPositions);
        
        setPositions(deduplicatedDefaultPositions);
      }
    } catch (error) {
      console.error('Error loading depth chart:', error);
    }
  }, [currentTeam?.id, activeTab, formations.selectedFormation, sortPositions, getSportRuleset, isStandardFormation, getDefaultPositionsForCurrentTab, dedupePositionsByKey, getDatabaseUnitForTab]);

  // Save depth chart
  const saveDepthChart = useCallback(async () => {
    if (!currentTeam?.id) {
      toast.error('No team selected');
      return;
    }
    
    setIsSaving(true);
    
    try {
      const sportRuleset = getSportRuleset();
      if (!sportRuleset) {
        console.error('No sport ruleset found for:', currentTeam.sport);
        return;
      }

      const entriesToSave = depthChart.filter(entry => entry.unit === activeTab);
      const databaseUnit = getDatabaseUnitForTab(activeTab);
      console.log('[saveDepthChart] Starting save process:');
      console.log('  - Team ID:', currentTeam.id);
      console.log('  - Active Tab:', activeTab);
      console.log('  - Selected Formation:', formations.selectedFormation);
      console.log('  - Sport uses formations:', sportRuleset.formations.enabled);
      console.log('  - Total depth chart entries:', depthChart.length);
      console.log('  - Entries to save for this tab:', entriesToSave.length);
      
      // First, delete all existing entries for this tab to avoid conflicts
      let deleteQuery = supabase
        .from('depth_chart')
        .delete()
        .eq('team_id', currentTeam.id)
        .eq('unit', databaseUnit);
      
      // For sports that use formations, only delete entries for the specific formation
      if (sportRuleset.formations.enabled && formations.selectedFormation) {
        deleteQuery = deleteQuery.eq('formation', formations.selectedFormation);
        console.log('[saveDepthChart] Deleting entries for formation:', formations.selectedFormation);
      } else {
        console.log('[saveDepthChart] Deleting all entries for tab (no formation filter)');
      }
      
      const { error: deleteError } = await deleteQuery;
      
      if (deleteError) {
        console.error('Error deleting existing entries:', deleteError);
        throw deleteError;
      }
      
      if (entriesToSave.length === 0) {
        toast.success('Depth chart saved successfully', { duration: 1500 });
        return;
      }
      
      // Prepare entries for saving
      const dataToSave = entriesToSave.map(entry => {
        const baseData: any = {
          team_id: currentTeam.id,
          position: entry.position,
          depth: entry.depth,
          // persisted unit value (may differ from UI tab, e.g. Team -> Offense)
          unit: databaseUnit,
          player_id: entry.player_id,
          color: entry.color || ''
        };

        // Add formation only if sport uses formations AND there's a selected formation
        if (sportRuleset.formations.enabled && formations.selectedFormation) {
          baseData.formation = formations.selectedFormation;
        }
        
        // No special teams unit needed - formation field handles this
        
        return baseData;
      });
      
      console.log('[saveDepthChart] Data to save:', dataToSave.length, 'entries');
      if (dataToSave.length > 0) {
        console.log('[saveDepthChart] Sample entry:', dataToSave[0]);
      }
      
      // Insert all entries
      const { error: insertError } = await supabase
        .from('depth_chart')
        .insert(dataToSave);
      
      if (insertError) {
        console.error('Insert error:', insertError);
        throw insertError;
      }
      
      console.log('[saveDepthChart] Successfully saved', dataToSave.length, 'entries');
      toast.success('Depth chart saved successfully', { duration: 1500 });
    } catch (error) {
      console.error('Error saving depth chart:', error);
      toast.error('Failed to save depth chart', { duration: 2000 });
    } finally {
      setIsSaving(false);
    }
  }, [currentTeam, activeTab, depthChart, formations.selectedFormation, getSportRuleset, getDatabaseUnitForTab]);

  // Save depth chart with specific data (bypasses state dependency)
  const saveDepthChartWithData = useCallback(async (entriesToSave: DepthChartEntry[]) => {
    if (!currentTeam?.id) {
      toast.error('No team selected');
      return;
    }
    
    setIsSaving(true);
    
    try {
      const sportRuleset = getSportRuleset();
      if (!sportRuleset) {
        console.error('No sport ruleset found for:', currentTeam.sport);
        return;
      }

      const filteredEntries = entriesToSave.filter(entry => entry.unit === activeTab);
      const databaseUnit = getDatabaseUnitForTab(activeTab);
      console.log('[saveDepthChartWithData] Starting save process:');
      console.log('  - Team ID:', currentTeam.id);
      console.log('  - Active Tab:', activeTab);
      console.log('  - Selected Formation:', formations.selectedFormation);
      console.log('  - Sport uses formations:', sportRuleset.formations.enabled);
      console.log('  - Total entries provided:', entriesToSave.length);
      console.log('  - Entries to save for this tab:', filteredEntries.length);
      
      // First, delete all existing entries for this tab to avoid conflicts
      let deleteQuery = supabase
        .from('depth_chart')
        .delete()
        .eq('team_id', currentTeam.id)
        .eq('unit', databaseUnit);
      
      // For sports that use formations, only delete entries for the specific formation
      if (sportRuleset.formations.enabled && formations.selectedFormation) {
        deleteQuery = deleteQuery.eq('formation', formations.selectedFormation);
        console.log('[saveDepthChartWithData] Deleting entries for formation:', formations.selectedFormation);
      } else {
        console.log('[saveDepthChartWithData] Deleting all entries for tab (no formation filter)');
      }
      
      const { error: deleteError } = await deleteQuery;
      
      if (deleteError) {
        console.error('Error deleting existing entries:', deleteError);
        throw deleteError;
      }
      
      if (filteredEntries.length === 0) {
        toast.success('Depth chart saved successfully', { duration: 1500 });
        return;
      }
      
      // Prepare entries for saving
      const dataToSave = filteredEntries.map(entry => {
        const baseData: any = {
          team_id: currentTeam.id,
          position: entry.position,
          depth: entry.depth,
          // persisted unit value (may differ from UI tab, e.g. Team -> Offense)
          unit: databaseUnit,
          player_id: entry.player_id,
          color: entry.color || ''
        };

        // Add formation only if sport uses formations AND there's a selected formation
        if (sportRuleset.formations.enabled && formations.selectedFormation) {
          baseData.formation = formations.selectedFormation;
        }
        
        // No special teams unit needed - formation field handles this
        
        return baseData;
      });
      
      console.log('[saveDepthChartWithData] Data to save:', dataToSave.length, 'entries');
      if (dataToSave.length > 0) {
        console.log('[saveDepthChartWithData] Sample entry:', dataToSave[0]);
      }
      
      // Insert all entries
      const { error: insertError } = await supabase
        .from('depth_chart')
        .insert(dataToSave);
      
      if (insertError) {
        console.error('Insert error:', insertError);
        throw insertError;
      }
      
      console.log('[saveDepthChartWithData] Successfully saved', dataToSave.length, 'entries');
      toast.success('Depth chart saved successfully', { duration: 1500 });
    } catch (error) {
      console.error('Error saving depth chart:', error);
      toast.error('Failed to save depth chart', { duration: 2000 });
    } finally {
      setIsSaving(false);
    }
  }, [currentTeam, activeTab, formations.selectedFormation, getSportRuleset, getDatabaseUnitForTab]);

  // Handle position color change - SIMPLIFIED
  const handlePositionColorChange = useCallback(async (positionKey: string, depth: number, color: string, _inning?: number) => {
    const databaseUnit = getDatabaseUnitForTab(activeTab);
    if (!currentTeam?.id) return;

    console.log('[handlePositionColorChange] Called with:', { positionKey, depth, color, activeTab, formation: formations.selectedFormation });

    const normalizedPositionKey = positionKey.toUpperCase();
    const isTemplateFormation = isStandardFormation(formations.selectedFormation);

    try {
      // Build query to check if entry exists for this specific formation
      let query = supabase
        .from('depth_chart')
        .select('*')
        .eq('team_id', currentTeam.id)
        .eq('position', normalizedPositionKey)
        .eq('depth', depth)
        .eq('unit', databaseUnit);

      // Add formation filter if sport uses formations
      const sportRuleset = getSportRuleset();
      if (sportRuleset?.formations.enabled && formations.selectedFormation) {
        query = query.eq('formation', formations.selectedFormation);
      }

      const { data: existingEntries } = await query;
      const existingEntry = existingEntries?.[0];

      if (existingEntry) {
        // Update existing entry - preserve position_full_name
        console.log('[handlePositionColorChange] Updating existing entry');
        const { error } = await supabase
          .from('depth_chart')
          .update({ 
            color,
            formation: formations.selectedFormation || '',
            is_template: isTemplateFormation
          })
          .eq('id', existingEntry.id);

        if (error) throw error;
      } else {
        // Create new entry - find position from current positions to get fullName
        console.log('[handlePositionColorChange] Creating new entry');
        const currentPosition = positions.find(p => p.key === normalizedPositionKey);
        
        // Prepare insert data
        const insertData: any = {
          team_id: currentTeam.id,
          position: normalizedPositionKey,
          depth,
          // persisted unit value (may differ from UI tab, e.g. Team -> Offense)
          unit: databaseUnit,
          color,
          formation: formations.selectedFormation || '',
          position_full_name: currentPosition?.fullName || normalizedPositionKey,
          is_template: isTemplateFormation
        };
        
        // No special teams unit needed - formation field handles this
        
        const { error } = await supabase
          .from('depth_chart')
          .insert(insertData);

        if (error) throw error;
      }

      console.log('[handlePositionColorChange] Success - updating UI');
      
      // Update local positions state immediately for instant UI feedback
      setPositions(prev => prev.map(pos => 
        pos.key === normalizedPositionKey 
          ? { ...pos, color } 
          : pos
      ));

      // Reload depth chart data
      await loadDepthChart();

    } catch (error) {
      console.error('Error updating position color:', error);
    }
  }, [currentTeam?.id, activeTab, formations.selectedFormation, loadDepthChart, getSportRuleset, isStandardFormation, getDatabaseUnitForTab]);

  // Handle player change - SIMPLIFIED
  const handlePlayerChange = useCallback(async (
    positionKey: string,
    depth: number,
    playerId: string | null,
    _inning?: number,
    meta?: { compactAfterClear?: boolean }
  ) => {
    const databaseUnit = getDatabaseUnitForTab(activeTab);
    if (!currentTeam?.id) return;

    console.log('[handlePlayerChange] Called with:', { positionKey, depth, playerId, formation: formations.selectedFormation });

    const normalizedPositionKey = positionKey.toUpperCase();

    /** "Clear this spot" in batting lineup: remove slot N and shift later slots up (same as compacted batting order UX). */
    if (
      meta?.compactAfterClear &&
      playerId === null &&
      depth === 1 &&
      databaseUnit === 'Lineup' &&
      (currentTeam?.sport === 'Baseball' || currentTeam?.sport === 'Softball')
    ) {
      const clearedOrder = parseInt(normalizedPositionKey, 10);
      if (Number.isFinite(clearedOrder) && clearedOrder >= 1) {
        try {
          let listQuery = supabase
            .from('depth_chart')
            .select('*')
            .eq('team_id', currentTeam.id)
            .eq('depth', depth)
            .eq('unit', databaseUnit);

          const sportRuleset = getSportRuleset();
          if (sportRuleset?.formations.enabled && formations.selectedFormation) {
            listQuery = listQuery.eq('formation', formations.selectedFormation);
          }

          const { data: lineupRows, error: listErr } = await listQuery;
          if (listErr) throw listErr;

          const numericRows = (lineupRows || []).filter(r => /^\d+$/.test(String(r.position).trim()));
          const sortedAsc = [...numericRows].sort(
            (a, b) => parseInt(String(a.position), 10) - parseInt(String(b.position), 10)
          );

          const atCleared = sortedAsc.find(r => parseInt(String(r.position), 10) === clearedOrder);

          const toShift = sortedAsc.filter(r => parseInt(String(r.position), 10) > clearedOrder);

          if (atCleared) {
            const { error: delErr } = await supabase.from('depth_chart').delete().eq('id', atCleared.id);
            if (delErr) throw delErr;
          }

          const decending = [...toShift].sort(
            (a, b) => parseInt(String(b.position), 10) - parseInt(String(a.position), 10)
          );
          for (const row of decending) {
            const oldNum = parseInt(String(row.position), 10);
            const newNum = oldNum - 1;
            const newKey = String(newNum).toUpperCase();
            const currentPosition = positions.find(p => p.key === newKey);
            const { error: upErr } = await supabase
              .from('depth_chart')
              .update({
                position: newKey,
                position_full_name: currentPosition?.fullName || newKey
              })
              .eq('id', row.id);
            if (upErr) throw upErr;
          }

          console.log('[handlePlayerChange] Lineup compact-after-clear succeeded');
          await loadDepthChart();
        } catch (error) {
          console.error('[handlePlayerChange] Lineup compact failed:', error);
        }
        return;
      }
    }
    const isTemplateFormation = isStandardFormation(formations.selectedFormation);

    // Check for duplicate player assignment if a player is being assigned
    if (playerId) {
      const sportRuleset = getSportRuleset();
      
      // Get all current depth chart entries for this depth and unit
      let duplicateCheckQuery = supabase
        .from('depth_chart')
        .select('*')
        .eq('team_id', currentTeam.id)
        .eq('depth', depth)
        .eq('unit', databaseUnit)
        .eq('player_id', playerId);

      // Add formation filter if sport uses formations
      if (sportRuleset?.formations.enabled && formations.selectedFormation) {
        duplicateCheckQuery = duplicateCheckQuery.eq('formation', formations.selectedFormation);
      }

      const { data: duplicateEntries } = await duplicateCheckQuery;
      
      // Skip warnings for baseball/softball (lineup doesn't use depth strings)
      const isBaseballOrSoftball = currentTeam?.sport === 'Baseball' || currentTeam?.sport === 'Softball';
      
      if (duplicateEntries && duplicateEntries.length > 0 && !isBaseballOrSoftball) {
        // Check if the player is already assigned to a different position at this depth
        const existingAssignment = duplicateEntries.find(entry => entry.position !== normalizedPositionKey);
        
        if (existingAssignment) {
          const player = players.find(p => p.id === playerId);
          const playerName = player ? `${player.first_name} ${player.last_name}` : 'Unknown Player';
          
          toast.error(
            `Warning: ${playerName} is already assigned to position ${existingAssignment.position} at this depth level. This may cause conflicts.`,
            { duration: 5000 }
          );
          
          // Still proceed with the assignment, but warn the coach
          console.log(`[handlePlayerChange] Warning: Player ${playerName} assigned to multiple positions at depth ${depth}`);
        }
      }

      // Check if the player is already assigned to the same position at a different depth
      let samePositionCheckQuery = supabase
        .from('depth_chart')
        .select('*')
        .eq('team_id', currentTeam.id)
        .eq('position', normalizedPositionKey)
        .eq('unit', databaseUnit)
        .eq('player_id', playerId);

      // Add formation filter if sport uses formations
      if (sportRuleset?.formations.enabled && formations.selectedFormation) {
        samePositionCheckQuery = samePositionCheckQuery.eq('formation', formations.selectedFormation);
      }

      const { data: samePositionEntries } = await samePositionCheckQuery;
      
      if (samePositionEntries && samePositionEntries.length > 0 && !isBaseballOrSoftball) {
        // Check if the player is already assigned to this position at a different depth
        const existingSamePosition = samePositionEntries.find(entry => entry.depth !== depth);
        
        if (existingSamePosition) {
          const player = players.find(p => p.id === playerId);
          const playerName = player ? `${player.first_name} ${player.last_name}` : 'Unknown Player';
          const depthName = existingSamePosition.depth === 1 ? '1st string' : 
                           existingSamePosition.depth === 2 ? '2nd string' : 
                           existingSamePosition.depth === 3 ? '3rd string' : 
                           `${existingSamePosition.depth}th string`;
          const newDepthName = depth === 1 ? '1st string' : 
                              depth === 2 ? '2nd string' : 
                              depth === 3 ? '3rd string' : 
                              `${depth}th string`;
          
          toast(
            `Warning: ${playerName} is already assigned to ${normalizedPositionKey} at ${depthName}. It is highly recommended to assign a different player as ${newDepthName} to ensure proper replacements work correctly during injuries or absences.`,
            { 
              duration: 6000,
              style: {
                background: '#fbbf24',
                color: '#1f2937',
                border: '1px solid #f59e0b'
              }
            }
          );
          
          // Still proceed with the assignment, but warn the coach
          console.log(`[handlePlayerChange] Warning: Player ${playerName} assigned to same position ${normalizedPositionKey} at multiple depths`);
        }
      }
    }

    try {
      // Build query to check if entry exists for this specific formation
      let query = supabase
        .from('depth_chart')
        .select('*')
        .eq('team_id', currentTeam.id)
        .eq('position', normalizedPositionKey)
        .eq('depth', depth)
        .eq('unit', databaseUnit);

      // Add formation filter if sport uses formations
      const sportRuleset = getSportRuleset();
      if (sportRuleset?.formations.enabled && formations.selectedFormation) {
        query = query.eq('formation', formations.selectedFormation);
      }

      const { data: existingEntries } = await query;
      const existingEntry = existingEntries?.[0];

      if (existingEntry) {
        // Update existing entry - preserve position_full_name
        console.log('[handlePlayerChange] Updating existing entry');
        const { error } = await supabase
          .from('depth_chart')
          .update({ 
            player_id: playerId,
            formation: formations.selectedFormation || '',
            is_template: isTemplateFormation
          })
          .eq('id', existingEntry.id);

        if (error) throw error;
      } else {
        // Create new entry - find position from current positions to get fullName
        console.log('[handlePlayerChange] Creating new entry');
        const currentPosition = positions.find(p => p.key === normalizedPositionKey);
        
        // Prepare insert data
        const insertData: any = {
          team_id: currentTeam.id,
          position: normalizedPositionKey,
          depth,
          // persisted unit value (may differ from UI tab, e.g. Team -> Offense)
          unit: databaseUnit,
          player_id: playerId,
          formation: formations.selectedFormation || '',
          position_full_name: currentPosition?.fullName || normalizedPositionKey,
          is_template: isTemplateFormation
        };
        
        // No special teams unit needed - formation field handles this
        
        const { error } = await supabase
          .from('depth_chart')
          .insert(insertData);

        if (error) throw error;
      }

      console.log('[handlePlayerChange] Success - reloading data');
      await loadDepthChart();

    } catch (error) {
      console.error('Error updating player:', error);
    }
  }, [currentTeam?.id, activeTab, formations.selectedFormation, loadDepthChart, getSportRuleset, getDatabaseUnitForTab, isStandardFormation, players, positions]);

  // Save custom position label
  const saveCustomPositionLabel = useCallback(async (positionKey: string, newName: string, newFullName?: string) => {
    if (!currentTeam) return false;
    
    // Normalize position key to uppercase for database consistency
    const normalizedPositionKey = positionKey.toUpperCase();
    const normalizedNewName = newName.toUpperCase();
    
    try {
      // Update the depth chart entry with the new position label
      const { error: depthChartError } = await supabase
        .from('depth_chart')
        .update({
          position: normalizedNewName // Simply update the position column with the new label
        })
        .eq('team_id', currentTeam.id)
        .eq('position', normalizedPositionKey)
        .eq('unit', activeTab);

      if (depthChartError) throw depthChartError;

      // Update local positions state
      const updatedPositions = positions.map(pos => 
        pos.key === normalizedPositionKey 
          ? { ...pos, name: normalizedNewName, fullName: newFullName || normalizedNewName }
          : pos
      );
      
      // Ensure no duplicate position keys
      const deduplicatedUpdatedPositions = updatedPositions.filter((position, index, array) => 
        array.findIndex(p => p.key === position.key) === index
      );
      
      setPositions(deduplicatedUpdatedPositions);

      return true;
    } catch (error) {
      console.error('Error saving position label:', error);
      toast.error('Failed to save position label', { duration: 2000 });
      return false;
    }
  }, [currentTeam, activeTab, positions]);

  // Initialize depth chart with positions - SIMPLIFIED
  const initializeDepthChart = useCallback(async (formationPositions: Position[]) => {
    console.log('[initializeDepthChart] Setting positions:', formationPositions.length);
    
    // Ensure no duplicate position keys
    const deduplicatedFormationPositions = formationPositions.filter((position, index, array) => 
      array.findIndex(p => p.key === position.key) === index
    );
    
    setPositions(deduplicatedFormationPositions);
    await loadDepthChart(deduplicatedFormationPositions);
  }, [loadDepthChart]);

  // Load data when team or tab changes
  useEffect(() => {
    if (currentTeam) {
      console.log('[useDepthChart useEffect] Team/tab change triggered reload - Team:', currentTeam.id, 'Tab:', activeTab);
      const loadData = async () => {
        await fetchPlayers();
        await fetchCustomPositions();
        await loadDepthChart();
      };
      loadData();
    }
  }, [currentTeam, activeTab, fetchPlayers, fetchCustomPositions, loadDepthChart]);

  // Effect to load depth chart when formation changes
  useEffect(() => {
    if (currentTeam?.id) {
      console.log('[useDepthChart useEffect] Formation change triggered reload - Formation:', formations.selectedFormation);
      loadDepthChart();
    }
  }, [currentTeam?.id, formations.selectedFormation, loadDepthChart]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  // Handle depth change
  const handleDepthChange = useCallback((depth: number) => {
    setSelectedDepth(depth);
  }, []);

  // Debounced save function
  const debouncedSave = useCallback(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    saveTimeoutRef.current = setTimeout(() => {
      saveDepthChart();
    }, 1000); // Debounce for 1 second
  }, [saveDepthChart]);



  // Helper function to filter players by position label using position mappings
  const getFilteredPlayersForPosition = useCallback(async (
    positionLabel: string
  ): Promise<Player[]> => {
    if (!currentTeam) return players;

    try {
      // Get position groups mapped to this label
      const mappedGroups = await getPositionGroupsForLabel(
        currentTeam.id,
        currentTeam.sport,
        activeTab as 'Offense' | 'Defense' | 'Special Teams',
        positionLabel,
        undefined, // formationId - could be enhanced to pass this
        formations.selectedFormation || undefined
      );

      // If no mappings found, return all players (backward compatibility)
      if (mappedGroups.length === 0) {
        return players;
      }

      // Filter players who have at least one of the mapped position groups
      return players.filter(player => {
        const playerGroups = (player as any).eligible_positions || [];
        return playerGroups.some((group: string) => mappedGroups.includes(group));
      });
    } catch (error) {
      console.error('Error filtering players by position:', error);
      return players; // Return all players on error
    }
  }, [currentTeam, activeTab, formations.selectedFormation, players]);

  return {
    // State
    players,
    depthChart,
    positions,
    customPositions,
    isLoading,
    isSaving,
    selectedDepth,
    
    // Actions
    handlePlayerChange,
    handlePositionColorChange,
    initializeDepthChart,
    fetchPlayers,
    fetchCustomPositions,
    loadDepthChart,
    saveDepthChart,
    debouncedSave,
    saveCustomPositionLabel,
    saveDepthChartWithData,
    getFilteredPlayersForPosition,
    
    // Setters for external control
    setPlayers,
    setPositions,
    setCustomPositions,
    setDepthChart,
    handleDepthChange
  };
}; 